import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { IsotoposInstalacionType } from "../../types/genericTables.types";
import useAuth from "../../context/AuthContext";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { CreatePageList } from "@app/components/Layouts/PageList";
import { Loading, MButton } from "@app/components";
import MessageDialog from "@app/components/Login/MessageDialog";
import { errorAlert } from "@app/utils/alerts";
import BackgroundForm from "./components/backgoundForm";
import { IFormNewBackground } from "@app/types/background.types";
import { useBackground } from "./utils/useBackground";
import {
  getDefaultValuesFondos,
  mapBackgroundForm,
  processDataAlfaBeta,
  applyBackgroundCasuistics,
} from "./utils/helpers";
import { PROCESS_OPTIONS_VALUE } from "./utils/constans";

interface EditUncertaintyProps {
  mode: MODES_OPTIONS;
}
const EditBackgound = ({ mode }: EditUncertaintyProps) => {
  const { idBackground } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isView = mode === MODES_OPTIONS.VIEW;

  const [loading, setLoading] = useState(true);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [dataToSave, setDataToSave] = useState<IFormNewBackground | null>(null);
  const { save, get, loadingSave, fondosList } = useBackground();

  const form = useForm<IFormNewBackground>({
    mode: "all",
  });

  const { reset } = form;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (idBackground) {
          const background = get(Number(idBackground));
          if (!background) {
            errorAlert("Fondo no encontrado");
            navigate("/admin/fondos");
            return;
          }
          const defaultValues = await getDefaultValuesFondos(background, user);
          reset(defaultValues);
        } else {
          const defaultValues = await getDefaultValuesFondos(undefined, user);
          reset(defaultValues);
        }
      } catch (error) {
        errorAlert("Error al cargar los valores predeterminados");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [idBackground, reset, navigate, user]);

  const submit = async (data: IFormNewBackground) => {
    let submintData = {};
    if (
      typeof data.type === "object" &&
      data.type.value !== PROCESS_OPTIONS_VALUE.GAMMA
    ) {
      if (
        !data.rowsCalculation?.length ||
        !data.rowsCalculation.some(({ cpm1, cpm2, cpm3, cpm4 }) =>
          [cpm1, cpm2, cpm3, cpm4].some((value) => Number(value) !== 0)
        )
      ) {
        errorAlert("No se encontraron datos en las tablas");
        return;
      }

      const calculationDate = data.calculation_date
        ? typeof data.calculation_date === "string"
          ? data.calculation_date
          : data.calculation_date.toISOString()
        : "";

      const processedData = processDataAlfaBeta(
        data.results || [],
        data.rowsCalculation,
        calculationDate
      );

      submintData = {
        ...data,
        fondo_calculation: processedData,
        code: data?.code?.id,
      };
    } else {
      
      const processedTableData = data.tableData.map((item) => {
        const isotopeId = 
          typeof item.isotope_instalation === "object" 
            ? item.isotope_instalation?.id 
            : item.isotope_instalation;
        
        const casuisticsResult = applyBackgroundCasuistics(
          item,
          fondosList,
          isotopeId
        );

        return {
          ...item,
          ...casuisticsResult,
          code: data?.code?.id,
          id: typeof item.id === "string" ? undefined : item.id,
          isotope_instalation:
            (item.isotope_instalation as IsotoposInstalacionType)?.id ||
            undefined,
        };
      });

      submintData = {
        ...data,
        fondo_calculation: processedTableData,
      };
    }
    const status = await save(
      mapBackgroundForm(submintData as IFormNewBackground)
    );
    if (!status) {
      navigate(-1);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSave = (data: IFormNewBackground) => {
    if (idBackground) {
      setDataToSave(data);
      setOpenConfirmModal(true);
    } else {
      submit(data);
    }
  };

  const handleDialogClose = () => setOpenConfirmModal(false);

  const handleDialogConfirm = () => {
    if (dataToSave) {
      submit(dataToSave);
    }
    handleDialogClose();
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSave)}>
          <CreatePageList
            title={
              idBackground ? `Editar Fondo - ${idBackground}` : "Nuevo Fondo"
            }
            footer={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                {!isView && (
                  <MButton
                    variant="contained"
                    color="primary"
                    text="Guardar"
                    type="submit"
                  />
                )}
                <MButton
                  variant="outlined"
                  color="secondary"
                  sx={{ ml: 2 }}
                  text="Cancelar"
                  onClick={handleBack}
                />
              </Box>
            }
          >
            <BackgroundForm mode={mode} />
          </CreatePageList>
        </form>
      </FormProvider>
      {loadingSave && <Loading />}
      <MessageDialog
        data={{
          component: "Confirm",
          title: "OJO: Va a proceder a Editar este fondo",
          message: "¿Desea continuar?",
          status: "confirm",
          active: openConfirmModal,
          handleConfirm: handleDialogConfirm,
        }}
        handleClose={handleDialogClose}
        handleConfirm={handleDialogConfirm}
      ></MessageDialog>
    </>
  );
};

export default EditBackgound;
